import { Box, useTheme } from '@mui/material';
import { TextSkeleton } from './skeleton';

interface DatabaseTableSkeletonProps {
  columns?: number;
  rows?: number;
  showActions?: boolean;
  title?: string;
  subtitle?: string;
}

export const DatabaseTableSkeleton = ({
  columns = 4,
  rows = 5,
  showActions = true,
  title,
  subtitle
}: DatabaseTableSkeletonProps) => {
  const theme = useTheme();

  return (
    <Box sx={{
      background: 'transparent',
      borderRadius: 0,
      p: 0,
      maxWidth: '100%',
      margin: '0 auto',
    }}>

      {(title || subtitle) && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, mt: 1 }}>
          <Box>
            {title && (
              <TextSkeleton width={200} height={32} />
            )}
            {subtitle && (
              <Box sx={{ mt: 0.5 }}>
                <TextSkeleton width={300} height={20} />
              </Box>
            )}
          </Box>
        </Box>
      )}


      <Box sx={{
        width: '100%',
        background: theme.palette.background.paper,
        borderRadius: 2,
        minHeight: 180,
        boxShadow: '0 2px 8px 0 rgba(16,30,54,0.04)',
        overflow: 'hidden',
      }}>
        {/* Column Headers */}
        <Box sx={{
          display: 'flex',
          borderBottom: `1px solid ${theme.palette.divider}`,
          py: 1,
          px: 2,
        }}>
          {Array.from({ length: columns }).map((_, index) => (
            <Box key={`header-${index}`} sx={{ flex: 1, px: 1 }}>
              <TextSkeleton width="80%" height={20} />
            </Box>
          ))}
          {showActions && (
            <Box sx={{ width: 80, px: 1 }}>
              <TextSkeleton width="100%" height={20} />
            </Box>
          )}
        </Box>

        {/* Table Rows */}
        {Array.from({ length: rows }).map((_, rowIndex) => (
          <Box
            key={`row-${rowIndex}`}
            sx={{
              display: 'flex',
              borderBottom: rowIndex === rows - 1 ? 'none' : `1px solid ${theme.palette.divider}`,
              py: 1,
              px: 2,
              alignItems: 'center',
              minHeight: 48,
            }}
          >
            {Array.from({ length: columns }).map((_, colIndex) => (
              <Box key={`cell-${rowIndex}-${colIndex}`} sx={{ flex: 1, px: 1 }}>
                <TextSkeleton width="90%" height={16} />
              </Box>
            ))}
            {showActions && (
              <Box sx={{ width: 80, px: 1, display: 'flex', gap: 1, justifyContent: 'center' }}>
                <TextSkeleton width={24} height={24} />
                <TextSkeleton width={24} height={24} />
                <TextSkeleton width={24} height={24} />
              </Box>
            )}
          </Box>
        ))}
        {/* Fake Pagination Bar */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', px: 2, py: 1, minHeight: 40 }}>
          <TextSkeleton width={60} height={16} />
          <Box sx={{ mx: 2, display: 'flex', gap: 1 }}>
            <TextSkeleton width={24} height={24} />
            <TextSkeleton width={24} height={24} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}; 